// Éditeur de lignes partagé par les devis (et, plus tard, en lecture seule par
// les commandes) : tableau de produits + récapitulatif financier épinglé,
// recalculé à chaque frappe avec le même moteur que la base (lib/totals).
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { money } from "@/lib/format";
import { computeTotals, lineTotal, type DiscountType } from "@/lib/totals";
import { useFeeTypes } from "@/modules/settings/SettingsPage";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export type Currency = "EUR" | "USD";

export interface EditorLine {
  key: string;
  product_id: string | null;
  sku: string | null;
  label: string;
  description: string;
  quantity: number;
  unit_price: number;
  catalog_price: number | null;
  price_source: "catalog" | "client_price" | "manual";
  discount_type: DiscountType;
  discount_value: number | null;
}
export interface EditorFee { key: string; fee_type_id: string | null; label: string; amount: number }
export interface EditorDoc {
  currency: Currency;
  lines: EditorLine[];
  fees: EditorFee[];
  global_discount_type: DiscountType;
  global_discount_value: number | null;
  tax_rate_pct: number;
  tax_label: string | null;
  deposit_pct: number;
}

const newKey = () => Math.random().toString(36).slice(2, 10);
export const emptyLine = (): EditorLine => ({ key: newKey(), product_id: null, sku: null, label: "", description: "", quantity: 1, unit_price: 0, catalog_price: null, price_source: "manual", discount_type: null, discount_value: null });

/** Prix applicable pour un produit et un client (tarif négocié sinon catalogue). */
async function effectivePrice(clientId: string, productId: string, currency: Currency) {
  const { data, error } = await supabase.rpc("effective_unit_price", { _client_id: clientId, _product_id: productId, _currency: currency });
  if (error) throw error;
  return data === null ? null : Number(data);
}

export function DocumentEditor({ doc, onChange, clientId, readOnly = false, hideDeposit = false, afterTotal, totalLabel = "Total à payer" }: { doc: EditorDoc; onChange: (d: EditorDoc) => void; clientId: string; readOnly?: boolean; hideDeposit?: boolean; afterTotal?: ReactNode; totalLabel?: string }) {
  const { data: products } = useQuery({
    queryKey: ["products", "active"],
    queryFn: async () => { const { data, error } = await supabase.from("products").select("id, sku, name, unit_price_eur, unit_price_usd").eq("is_active", true).order("name"); if (error) throw error; return data; },
  });
  const { data: feeTypes } = useFeeTypes(true);
  const totals = useMemo(() => computeTotals({
    items: doc.lines, fees: doc.fees, global_discount_type: doc.global_discount_type, global_discount_value: doc.global_discount_value,
    tax_rate_pct: doc.tax_rate_pct, deposit_pct: doc.deposit_pct,
  }), [doc]);
  const cur = doc.currency;
  // Toujours partir du document le plus récent : le prix d'un produit arrive
  // après un appel réseau, et l'utilisateur a pu modifier la quantité entre-temps.
  // Sans cela, la mise à jour du prix écraserait sa saisie avec l'état d'avant.
  const docRef = useRef(doc);
  useEffect(() => { docRef.current = doc; });   // après le rendu : écrire une ref pendant le rendu fausserait les rendus concurrents
  const setLine = (key: string, patch: Partial<EditorLine>) => { const d = docRef.current; onChange({ ...d, lines: d.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }); };
  const setFee = (key: string, patch: Partial<EditorFee>) => { const d = docRef.current; onChange({ ...d, fees: d.fees.map((f) => (f.key === key ? { ...f, ...patch } : f)) }); };
  const move = (i: number, dir: -1 | 1) => { const a = [...doc.lines]; const j = i + dir; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j]!, a[i]!]; onChange({ ...doc, lines: a }); };

  async function pickProduct(key: string, productId: string) {
    const p = products?.find((x) => x.id === productId);
    if (!p) return;
    const catalog = cur === "EUR" ? p.unit_price_eur : p.unit_price_usd;
    let price = catalog === null ? 0 : Number(catalog);
    let source: EditorLine["price_source"] = "catalog";
    try {
      const eff = await effectivePrice(clientId, productId, cur);
      if (eff !== null && catalog !== null && eff !== Number(catalog)) { price = eff; source = "client_price"; }
      else if (eff !== null) price = eff;
    } catch { /* prix catalogue par défaut */ }
    setLine(key, { product_id: p.id, sku: p.sku, label: p.name, unit_price: price, catalog_price: catalog === null ? null : Number(catalog), price_source: source });
  }

  const num = (v: string) => (v === "" ? 0 : Number(v));

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
      {/* min-w-0 : sans lui, le tableau des lignes élargit la colonne au-delà de l'écran (téléphone) */}
      <div className="min-w-0 space-y-6">
        {/* Lignes */}
        <section className="glass overflow-hidden rounded-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="min-w-[260px] px-3 py-2 text-left">Produit</th>
                  <th className="w-24 px-2 py-2 text-right">Qté</th>
                  <th className="w-32 px-2 py-2 text-right">Prix unit.</th>
                  <th className="w-44 px-2 py-2 text-left">Remise</th>
                  <th className="w-28 px-2 py-2 text-right">Montant</th>
                  {!readOnly && <th className="w-24" />}
                </tr>
              </thead>
              <tbody>
                {doc.lines.map((l, i) => (
                  <tr key={l.key} className="border-t border-border/70 align-top">
                    <td className="px-3 py-2">
                      {readOnly ? (
                        <div><div className="font-medium">{l.label}</div>{l.sku && <div className="font-mono text-xs text-muted-foreground">{l.sku}</div>}</div>
                      ) : (
                        <div className="space-y-1.5">
                          <Select value={l.product_id ?? "__free"} onValueChange={(v) => v === "__free" ? setLine(l.key, { product_id: null, sku: null, price_source: "manual", catalog_price: null }) : void pickProduct(l.key, v)}>
                            <SelectTrigger aria-label={`Produit ligne ${i + 1}`} className="h-9"><SelectValue placeholder="Choisir un produit" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__free">Ligne libre</SelectItem>
                              {products?.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input aria-label={`Libellé ligne ${i + 1}`} className="h-9" value={l.label} placeholder="Libellé" onChange={(e) => setLine(l.key, { label: e.target.value })} />
                          {l.price_source === "client_price" && <p className="text-xs text-primary">Tarif négocié du client (catalogue : {money(l.catalog_price, cur)})</p>}
                          {l.price_source === "catalog" && l.catalog_price !== null && l.unit_price !== l.catalog_price && <p className="text-xs text-amber-600">Prix modifié à la main (catalogue : {money(l.catalog_price, cur)})</p>}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">{readOnly ? Number(l.quantity) : <Input aria-label={`Quantité ligne ${i + 1}`} className="h-9 text-right" type="number" min="1" step="1" value={l.quantity} onChange={(e) => setLine(l.key, { quantity: num(e.target.value) })} />}</td>
                    <td className="px-2 py-2 text-right">{readOnly ? money(l.unit_price, cur) : <Input aria-label={`Prix unitaire ligne ${i + 1}`} className="h-9 text-right" type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => setLine(l.key, { unit_price: num(e.target.value), price_source: l.product_id ? (l.price_source === "client_price" ? "client_price" : "catalog") : "manual" })} />}</td>
                    <td className="px-2 py-2">
                      {readOnly ? (l.discount_type ? (l.discount_type === "percent" ? `−${Number(l.discount_value)} %` : `−${money(l.discount_value, cur)}`) : "—") : (
                        <div className="flex gap-1">
                          <Select value={l.discount_type ?? "none"} onValueChange={(v) => setLine(l.key, { discount_type: v === "none" ? null : (v as DiscountType), discount_value: v === "none" ? null : l.discount_value ?? 0 })}>
                            <SelectTrigger aria-label={`Type de remise ligne ${i + 1}`} className="h-9 w-20"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="percent">%</SelectItem><SelectItem value="amount">{cur === "EUR" ? "€" : "$"}</SelectItem></SelectContent>
                          </Select>
                          <Input aria-label={`Remise ligne ${i + 1}`} className="h-9 text-right" type="number" min="0" max={l.discount_type === "percent" ? 100 : undefined} step="0.01" disabled={!l.discount_type} value={l.discount_value ?? ""} onChange={(e) => setLine(l.key, { discount_value: e.target.value === "" ? null : Number(e.target.value) })} />
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-medium tabular-nums" data-testid={`line-total-${i + 1}`}>{money(lineTotal(l), cur)}</td>
                    {!readOnly && (
                      <td className="px-1 py-2">
                        <div className="flex justify-end">
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Monter" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="h-4 w-4" /></Button>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Descendre" disabled={i === doc.lines.length - 1} onClick={() => move(i, 1)}><ArrowDown className="h-4 w-4" /></Button>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" aria-label={`Supprimer ligne ${i + 1}`} onClick={() => onChange({ ...doc, lines: doc.lines.filter((x) => x.key !== l.key) })}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {doc.lines.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Aucune ligne. Ajoute un produit du catalogue ou une ligne libre.</td></tr>}
              </tbody>
            </table>
          </div>
          {!readOnly && (
            <div className="border-t border-border/70 p-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...doc, lines: [...doc.lines, emptyLine()] })}><Plus className="mr-1 h-4 w-4" />Ajouter une ligne</Button>
            </div>
          )}
        </section>

        {/* Frais */}
        <section className="glass rounded-2xl p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Frais</h3>
            {!readOnly && (
              <div className="flex gap-2">
                {feeTypes && feeTypes.length > 0 && (
                  <Select value="" onValueChange={(id) => { const t = feeTypes.find((f) => f.id === id); if (t) onChange({ ...doc, fees: [...doc.fees, { key: newKey(), fee_type_id: t.id, label: t.label, amount: t.default_amount === null ? 0 : Number(t.default_amount) }] }); }}>
                    <SelectTrigger aria-label="Ajouter un frais du paramétrage" className="h-8 w-56"><SelectValue placeholder="Ajouter un frais…" /></SelectTrigger>
                    <SelectContent>{feeTypes.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}{f.default_amount !== null ? ` — ${money(f.default_amount, cur)}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...doc, fees: [...doc.fees, { key: newKey(), fee_type_id: null, label: "", amount: 0 }] })}><Plus className="mr-1 h-4 w-4" />Frais libre</Button>
              </div>
            )}
          </div>
          {doc.fees.length === 0 ? <p className="text-sm text-muted-foreground">Aucun frais. Rien n'est ajouté automatiquement.</p> : (
            <ul className="space-y-2">
              {doc.fees.map((f, i) => (
                <li key={f.key} className="flex items-center gap-2">
                  {readOnly ? <span className="flex-1">{f.label}</span> : <Input aria-label={`Libellé frais ${i + 1}`} className="h-9 flex-1" value={f.label} placeholder="Libellé du frais" onChange={(e) => setFee(f.key, { label: e.target.value })} />}
                  {readOnly ? <span className="w-28 text-right tabular-nums">{money(f.amount, cur)}</span> : <Input aria-label={`Montant frais ${i + 1}`} className="h-9 w-32 text-right" type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setFee(f.key, { amount: num(e.target.value) })} />}
                  {!readOnly && <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" aria-label={`Supprimer frais ${i + 1}`} onClick={() => onChange({ ...doc, fees: doc.fees.filter((x) => x.key !== f.key) })}><Trash2 className="h-4 w-4" /></Button>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Récapitulatif épinglé */}
      <aside className="min-w-0 xl:sticky xl:top-6 xl:self-start">
        <div className="glass rounded-2xl p-5">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Récapitulatif</h3>
          <dl className="space-y-2 text-sm">
            <Row label="Sous-total" value={money(totals.subtotal, cur)} testId="subtotal" />
            {!readOnly && (
              <div className="rounded-xl bg-muted/60 p-2">
                <Label className="text-xs">Remise globale</Label>
                <div className="mt-1 flex gap-1">
                  <Select value={doc.global_discount_type ?? "none"} onValueChange={(v) => onChange({ ...doc, global_discount_type: v === "none" ? null : (v as DiscountType), global_discount_value: v === "none" ? null : doc.global_discount_value ?? 0 })}>
                    <SelectTrigger aria-label="Type de remise globale" className="h-9 w-24 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Aucune</SelectItem><SelectItem value="percent">%</SelectItem><SelectItem value="amount">{cur === "EUR" ? "€" : "$"}</SelectItem></SelectContent>
                  </Select>
                  <Input aria-label="Remise globale" className="h-9 text-right" type="number" min="0" max={doc.global_discount_type === "percent" ? 100 : undefined} step="0.01" disabled={!doc.global_discount_type} value={doc.global_discount_value ?? ""} onChange={(e) => onChange({ ...doc, global_discount_value: e.target.value === "" ? null : Number(e.target.value) })} />
                </div>
              </div>
            )}
            {(totals.global_discount_amount > 0 || readOnly) && <Row label={`Remise globale${doc.global_discount_type === "percent" ? ` (${doc.global_discount_value ?? 0} %)` : ""}`} value={`− ${money(totals.global_discount_amount, cur)}`} testId="global-discount" />}
            <Row label="Frais" value={money(totals.fees_total, cur)} testId="fees" />
            <Row label="Base" value={money(totals.taxable_base, cur)} testId="base" bold />
            <Row label={`Taxe${doc.tax_label ? ` — ${doc.tax_label}` : ""} (${doc.tax_rate_pct} %)`} value={money(totals.tax_amount, cur)} testId="tax" />
            <Row label={totalLabel} value={money(totals.total, cur)} testId="total" big />
            {afterTotal}
            {!hideDeposit && <div className="my-2 border-t border-border/70" />}
            {!hideDeposit && !readOnly && (
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="deposit_pct" className="text-xs">Acompte (%)</Label>
                <Input id="deposit_pct" className="h-9 w-24 text-right" type="number" min="0" max="100" step="1" value={doc.deposit_pct} onChange={(e) => onChange({ ...doc, deposit_pct: Math.min(100, Math.max(0, num(e.target.value))) })} />
              </div>
            )}
            {!hideDeposit && <Row label={`Acompte (${doc.deposit_pct} %)`} value={money(totals.deposit_amount, cur)} testId="deposit" />}
            {!hideDeposit && <Row label="Solde restant" value={money(totals.balance_amount, cur)} testId="balance" bold />}
          </dl>
        </div>
      </aside>
    </div>
  );
}

export function Row({ label, value, testId, bold, big }: { label: string; value: string; testId: string; bold?: boolean; big?: boolean }) {
  return (
    <div className={cn("flex items-baseline justify-between gap-3", bold && "font-semibold", big && "text-lg font-bold text-primary")}>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums" data-testid={`sum-${testId}`}>{value}</dd>
    </div>
  );
}
